import React, { ReactNode, useEffect, useMemo, useState } from 'react'
import { useTable, Column, Row } from 'react-table'
import { KTCardBody, showError } from '../../../_metronic/helpers'
import { ListLoading } from './ListLoading'
import { ModernTable } from './ModernTable'
import { TablePagination } from './TablePagination'
import { TableEmptyState, TableErrorState } from './TableStates'
import { ItemLabel } from './tableTypes'
import modernStyles from './styles/DataTable.module.css'

type CustomTableProps<T extends object> = {
  id: string
  data: T[]
  columns: readonly Column<T>[]
  isLoading?: boolean
  queryHooks?: {
    useQueryRequest: any
    useQueryResponse: any
  }
  enableExpand?: boolean
  renderExpand?: (data?: any, isLoading?: boolean) => React.ReactNode
  onExpandFetch?: (row: Row<T>) => Promise<any>
  notPaginated?: boolean
  /** Nome acessível da tabela. */
  ariaLabel?: string
  /** Rótulo do item no rodapé: "1–10 de 24 instituições". */
  itemLabel?: ItemLabel
  /** Conteúdo quando não há linhas. Use <TableEmptyState>. */
  emptyState?: ReactNode
  /** Falha na busca. */
  isError?: boolean
  onRetry?: () => void
  /** Destaca a linha selecionada. */
  isRowSelected?: (row: T) => boolean
  /**
   * Nome do registro no botão de expandir: "Mostrar detalhes de <nome>" (com enableExpand).
   * A coluna de id `expand` recebe o botão.
   */
  getExpandLabel?: (row: T) => string | undefined
}

export function CustomTable<T extends object>({
  id,
  data,
  columns,
  isLoading = false,
  queryHooks,
  enableExpand = false,
  renderExpand,
  onExpandFetch,
  notPaginated,
  ariaLabel,
  itemLabel,
  emptyState,
  isError,
  onRetry,
  isRowSelected,
  getExpandLabel,
}: CustomTableProps<T>) {
  const tableData = useMemo(() => data, [data])
  const tableColumns = useMemo(() => [...columns], [columns])

  const instance = useTable<T>({
    columns: tableColumns,
    data: tableData,
  })
  const { rows } = instance

  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({})
  const [expandData, setExpandData] = useState<{ [key: string]: any }>({})
  const [loadingExpand, setLoadingExpand] = useState<{ [key: string]: boolean }>({})

  const toggleRowExpansion = async (row: Row<T>) => {
    const rowId = String(row.id)

    const isCurrentlyExpanded = expandedRows[rowId]
    if (isCurrentlyExpanded) {
      setExpandedRows((prev) => ({
        ...prev,
        [rowId]: false,
      }))
    } else {
      setExpandedRows((prev) => ({
        ...prev,
        [rowId]: true,
      }))

      if (onExpandFetch && !expandData[rowId]) {
        setLoadingExpand((prev) => ({ ...prev, [rowId]: true }))
        try {
          const fetched = await onExpandFetch(row)
          setExpandData((prev) => ({ ...prev, [rowId]: fetched }))
        } catch (error) {
          showError(error)
        } finally {
          setLoadingExpand((prev) => ({ ...prev, [rowId]: false }))
        }
      }
    }
  }

  useEffect(() => {
    setExpandedRows({})
    setExpandData({})
    setLoadingExpand({})
  }, [data])

  return (
    <KTCardBody className={`py-4 w-100 ${modernStyles.surface}`}>
      <div className='table-responsive w-100'>
        <ModernTable<T>
          id={id}
          instance={instance}
          ariaLabel={ariaLabel}
          isLoading={isLoading}
          isError={isError}
          errorState={<TableErrorState onRetry={onRetry} />}
          emptyState={emptyState ?? <TableEmptyState title='Nenhum registro encontrado' />}
          isRowSelected={isRowSelected}
          useQueryRequest={queryHooks?.useQueryRequest}
          expansion={
            enableExpand && renderExpand
              ? {
                  isExpanded: (row) => !!expandedRows[String(row.id)],
                  onToggle: toggleRowExpansion,
                  render: (row) =>
                    renderExpand(expandData[String(row.id)], loadingExpand[String(row.id)]),
                  getRowLabel: getExpandLabel,
                }
              : undefined
          }
        />
      </div>

      {queryHooks && !notPaginated && !isError && (
        <TablePagination
          useQueryRequest={queryHooks.useQueryRequest}
          useQueryResponse={queryHooks.useQueryResponse}
          itemLabel={itemLabel}
        />
      )}

      {/* Refetch com dados na tela (troca de página/filtro): overlay; 1º carregamento: skeleton. */}
      {isLoading && rows.length > 0 && <ListLoading />}
    </KTCardBody>
  )
}
